import SolarPenBold from '@/components-v2/icons/SolarPenBold';
import SolarTrashBinTrashBold from '@/components-v2/icons/SolarTrashBinTrashBold';
import SolarUploadOutline from '@/components-v2/icons/SolarUploadOutline';
import { useToast } from '@/components/toast';
import { UPDATE_CANVAS_IMAGE_STATE_EVENT, UPDATE_PREVIEW_RATIO_EVENT, globalMitt } from '@/utils/global-mitt';
import { isMac } from '@/utils/helper';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslate } from '@tolgee/react';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { CropPanel } from '../crop-panel';
import { useDragAndDrop, useFileValidation, useOssUpload, usePasteHandler } from './hooks';
import type { DragDropFileUploadProps } from './index';
import { useUploadState } from './shared-hooks';
import { previewTextureBackground } from './texture-styles';
import { ASPECT_RATIO_LIMITS, isValidUrl } from './utils';
import { zoomFile } from './zoom-utils';

// 传统模式组件 - 使用提供的传统代码
const TraditionalModeComponent = forwardRef<HTMLDivElement, DragDropFileUploadProps>(
  (
    {
      onUploadComplete,
      onUploadError,
      size = 'medium',
      sx,
      initialAspectRatio = 1,
      minPixel = 0,
      maxPixel = Infinity,
      value,
      onChange,
      error,
      helperText,
    },
    ref,
  ) => {
    const { t } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { showToast } = useToast();

    // 统一的状态管理
    const { containerRef, loading, setLoading } = useUploadState({ ref });
    const { validateImageDimensions } = useFileValidation({ minPixel, maxPixel });
    const { uploadFile } = useOssUpload({ onUploadComplete, onUploadError });

    // 内部统一状态管理 - 替代useImagePreview和分散的状态
    const [imageState, setImageState] = useState<{
      preview: string | null;
      original: string | null;
      aspectRatio: number;
      width: number;
      height: number;
      pendingFile: File | null;
    }>({
      preview: null,
      original: null,
      aspectRatio: initialAspectRatio,
      width: 0,
      height: 0,
      pendingFile: null,
    });

    const [openCropModal, setOpenCropModal] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // 统一的状态更新函数
    const updateImageState = useCallback((updates: Partial<typeof imageState>) => {
      setImageState((prev) => ({ ...prev, ...updates }));
    }, []);

    // 统一的onChange通知函数
    const notifyChange = useCallback(
      (url: string, width: number, height: number) => {
        if (onChange) {
          onChange({ url, width, height });
        }
      },
      [onChange],
    );

    // 文件处理函数
    const handleTriggerFileInput = useCallback((e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }, []);

    const handleFileSelected = useCallback(
      async (file: File) => {
        setLoading(true);
        updateImageState({ pendingFile: file });

        const reader = new FileReader();
        reader.onloadend = () => {
          const imageDataUrl = reader.result as string;
          const img = new Image();

          img.onload = async () => {
            let width = img.width;
            let height = img.height;
            let ratio = width / height;

            let isValidate = true;

            console.log('maxPixel', maxPixel);

            try {
              isValidate = validateImageDimensions(width, height);
            } catch (error) {
              console.error(error);
              setLoading(false);
              updateImageState({ pendingFile: null });
            }

            if (!isValidate) {
              // 图片 zoom
              console.time('zoomFile');
              const zoomedFile = await zoomFile(file, { maxPixel });
              showToast(t('image.size.exceeded.auto_adjust'), 'success');

              file = zoomedFile.file;
              width = zoomedFile.scaledWidth;
              height = zoomedFile.scaledHeight;
              ratio = width / height;
              console.timeEnd('zoomFile');
            }

            // 检查比例是否在允许范围内
            if (ratio < ASPECT_RATIO_LIMITS.MIN || ratio > ASPECT_RATIO_LIMITS.MAX) {
              // 比例超出范围，显示裁剪对话框
              updateImageState({
                original: imageDataUrl,
                aspectRatio: ratio,
                width,
                height,
              });
              setOpenCropModal(true);
              showToast(t('image.info.crop_needed'), 'error');
            } else {
              // 比例在范围内，直接使用图片
              updateImageState({
                preview: imageDataUrl,
                original: imageDataUrl,
                aspectRatio: ratio,
                width,
                height,
              });

              // 通知有图片了和比例信息
              globalMitt.emit(UPDATE_CANVAS_IMAGE_STATE_EVENT, true);
              const ratioStr = `${Math.round(width)}:${Math.round(height)}`;
              globalMitt.emit(UPDATE_PREVIEW_RATIO_EVENT, ratioStr);

              // 自动上传到OSS并返回URL
              try {
                const uploadedUrl = await uploadFile(file, { width, height });
                updateImageState({ preview: uploadedUrl });
                notifyChange(uploadedUrl, width, height);
              } catch (error) {
                // 上传失败时使用本地dataURL作为fallback
                showToast(error instanceof Error ? error.message : 'image.error.load_failed', 'error');
                updateImageState({ pendingFile: null });
              }
            }

            setLoading(false);
          };

          img.onerror = () => {
            setLoading(false);
            showToast('image.error.load_failed', 'error');
            updateImageState({ pendingFile: null });
          };

          img.src = imageDataUrl;
        };

        reader.onerror = () => {
          setLoading(false);
          showToast('文件读取失败', 'error');
          updateImageState({ pendingFile: null });
        };

        reader.readAsDataURL(file);
      },
      [validateImageDimensions, setLoading, updateImageState, uploadFile, notifyChange, showToast, t],
    );

    // 简化的文件处理
    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;
        handleFileSelected(files[0]);
      },
      [handleFileSelected],
    );

    const handleFileDrop = useCallback(
      async (files: File[]) => {
        if (files[0]) {
          await handleFileSelected(files[0]);
        }
      },
      [handleFileSelected],
    );

    const { dragOver, handleDragOver, handleDragLeave, handleDrop } = useDragAndDrop({
      onFileDrop: handleFileDrop,
      multiple: false,
    });

    // 粘贴处理
    const { handlePaste } = usePasteHandler({
      onFilePaste: handleFileSelected,
    });

    // 同步外部value到内部状态
    useEffect(() => {
      if (value?.url && isValidUrl(value.url)) {
        updateImageState({
          preview: value.url,
          original: value.url,
          aspectRatio: value.width && value.height ? value.width / value.height : initialAspectRatio,
          width: value.width || 0,
          height: value.height || 0,
        });
      } else {
        // 外部value被清空时，重置内部状态
        updateImageState({
          preview: null,
          original: null,
          aspectRatio: initialAspectRatio,
          width: 0,
          height: 0,
          pendingFile: null,
        });
      }
    }, [value, initialAspectRatio, updateImageState]);

    // 添加 paste 事件监听器
    useEffect(() => {
      const handlePasteEvent = (event: ClipboardEvent) => {
        handlePaste(event);
      };

      // 监听全局 paste 事件
      document.addEventListener('paste', handlePasteEvent);

      return () => {
        document.removeEventListener('paste', handlePasteEvent);
      };
    }, [handlePaste]);

    const handleCropComplete = async (croppedImage: string) => {
      if (!croppedImage || croppedImage.length < 50 || croppedImage === 'data:,') {
        showToast('裁剪图片处理失败', 'error');
        return;
      }

      try {
        setLoading(true);

        // 从裁剪图像创建 Blob
        const mimeType = 'image/jpeg';
        const blob = await fetch(croppedImage).then((r) => {
          if (!r.ok) {
            throw new Error(`获取图片失败: ${r.status} ${r.statusText}`);
          }
          return r.blob();
        });

        if (blob.size === 0) {
          throw new Error('图片数据无效');
        }

        // 获取真实的图片尺寸并验证
        const img = new Image();
        const { width: imgWidth, height: imgHeight } = await new Promise<{ width: number; height: number }>(
          (resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error('Image loading timed out'));
            }, 5000);

            img.onload = () => {
              clearTimeout(timeout);

              if (img.width <= 0 || img.height <= 0) {
                reject(new Error('Loaded image has invalid dimensions'));
                return;
              }

              // 验证裁剪后的图片尺寸
              if (!validateImageDimensions(img.width, img.height)) {
                reject(new Error('Cropped image dimensions are not valid'));
                return;
              }

              resolve({ width: img.width, height: img.height });
            };

            img.onerror = (err) => {
              clearTimeout(timeout);
              reject(err);
            };

            img.src = URL.createObjectURL(blob);
          },
        );

        // 更新预览状态
        updateImageState({
          preview: croppedImage,
          width: imgWidth,
          height: imgHeight,
          aspectRatio: imgWidth > 0 && imgHeight > 0 ? imgWidth / imgHeight : initialAspectRatio,
        });

        // 通知有图片了和比例信息
        globalMitt.emit(UPDATE_CANVAS_IMAGE_STATE_EVENT, true);
        if (imgWidth > 0 && imgHeight > 0) {
          const ratio = `${Math.round(imgWidth)}:${Math.round(imgHeight)}`;
          globalMitt.emit(UPDATE_PREVIEW_RATIO_EVENT, ratio);
        }

        // 创建文件用于上传
        const originalFileName = imageState.pendingFile ? imageState.pendingFile.name : 'cropped-image.png';
        const fileNameWithoutExt = originalFileName.substring(0, originalFileName.lastIndexOf('.'));
        const fileExt = originalFileName.substring(originalFileName.lastIndexOf('.'));
        const timestamp = new Date().getTime();
        const newFileName = `${fileNameWithoutExt}_cropped_${timestamp}${fileExt}`;
        const file = new File([blob], newFileName, { type: mimeType });

        // 上传裁剪后的图片到 OSS
        try {
          const uploadedUrl = await uploadFile(file, { width: imgWidth, height: imgHeight });
          updateImageState({ preview: uploadedUrl });

          // 通知外部组件状态变化（使用OSS URL和真实尺寸）
          notifyChange(uploadedUrl, imgWidth, imgHeight);
        } catch (_error) {
          // 上传失败时使用本地dataURL作为fallback
          notifyChange(croppedImage, imgWidth, imgHeight);
        }
      } catch (error) {
        showToast(`处理图片时出错：${error instanceof Error ? error.message : '未知错误'}`, 'error');
        setOpenCropModal(false);
      } finally {
        setLoading(false);
      }
    };

    const handleCropCancel = useCallback(() => {
      setOpenCropModal(false);
      // 如果没有预览图片，清理所有临时状态
      if (!imageState.preview) {
        updateImageState({
          original: null,
          pendingFile: null,
        });
      }
    }, [imageState.preview, updateImageState]);

    // 处理清除图片
    const handleClearImage = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // 重置所有状态
        updateImageState({
          preview: null,
          original: null,
          aspectRatio: initialAspectRatio,
          width: 0,
          height: 0,
          pendingFile: null,
        });

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        // 重置预览状态
        setTimeout(() => {
          globalMitt.emit(UPDATE_CANVAS_IMAGE_STATE_EVENT, false);
          globalMitt.emit(UPDATE_PREVIEW_RATIO_EVENT, '1:1');
        }, 0);

        // 通知外部值已清空
        notifyChange('', 0, 0);
      },
      [updateImageState, initialAspectRatio, notifyChange],
    );

    // Calculate padding-top based on aspect ratio
    // For vertical images (ratio < 1), keep 100%
    // For horizontal images (ratio > 1), adjust according to actual ratio
    const paddingTop = imageState.aspectRatio < 1 ? '100%' : `${(1 / imageState.aspectRatio) * 100}%`;

    return (
      <>
        <Box
          ref={containerRef}
          data-upload-component="true"
          data-canvas-mode="false"
          sx={{ width: '100%', overflow: 'hidden', borderRadius: 2, ...sx }}
        >
          <Paper
            variant="outlined"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              border: `2px dashed ${dragOver ? theme.palette.primary.main : theme.palette.divider}`,
              borderRadius: 2,
              padding: size === 'small' ? 0.25 : 1,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: dragOver ? alpha(theme.palette.primary.main, 0.08) : 'background.neutral',
              position: 'relative',
              transition: theme.transitions.create(['border-color', 'background-color'], {
                duration: theme.transitions.duration.shorter,
              }),
            }}
          >
            <input
              ref={fileInputRef}
              accept="image/jpeg,image/png,image/gif,image/webp,image/bmp"
              style={{ display: 'none' }}
              id="raised-button-file"
              type="file"
              onChange={handleChange}
            />
            <label htmlFor="raised-button-file" style={{ display: 'block', cursor: 'pointer' }}>
              {imageState.preview ? (
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 2,
                    overflow: 'hidden',
                    backgroundColor: 'transparent',
                    boxShadow: `0 0 0 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                    paddingTop, // Using dynamic padding based on aspect ratio
                    '&:before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      ...previewTextureBackground,
                      pointerEvents: 'none',
                      zIndex: 0, // 确保在图片下面
                    },
                  }}
                >
                  {/* Preview Image */}
                  <Box
                    component="img"
                    src={imageState.preview}
                    alt="Preview"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      objectPosition: 'center',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      borderRadius: 'inherit',
                      zIndex: 1, // 确保在背景之上
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // 直接触发文件选择对话框
                      handleTriggerFileInput(e);
                    }}
                    onError={(_e) => {
                      // 可以在这里显示一个错误提示或设置一个默认图片
                    }}
                  />

                  {/* Fixed buttons like Canvas Mode */}
                  <IconButton
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (imageState.original) {
                        setOpenCropModal(true);
                      }
                    }}
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 88,
                      width: 32,
                      height: 32,
                      bgcolor: alpha(theme.palette.primary.main, 0.9),
                      color: 'common.white',
                      '@media (hover: hover)': {
                        '&:hover': {
                          bgcolor: theme.palette.primary.main,
                          transform: 'scale(1.1)',
                        },
                      },
                      transition: theme.transitions.create(['background-color', 'transform'], {
                        duration: theme.transitions.duration.shorter,
                      }),
                      zIndex: 10,
                    }}
                    title={t('image.edit')}
                  >
                    <SolarPenBold height={16} width={16} />
                  </IconButton>

                  <IconButton
                    onClick={handleTriggerFileInput}
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 48,
                      width: 32,
                      height: 32,
                      bgcolor: alpha(theme.palette.info.main, 0.9),
                      color: 'common.white',
                      '@media (hover: hover)': {
                        '&:hover': {
                          bgcolor: theme.palette.info.main,
                          transform: 'scale(1.1)',
                        },
                      },
                      transition: theme.transitions.create(['background-color', 'transform'], {
                        duration: theme.transitions.duration.shorter,
                      }),
                      zIndex: 10,
                    }}
                    title={t('image.replace')}
                  >
                    <SolarUploadOutline height={16} width={16} />
                  </IconButton>

                  <IconButton
                    onClick={handleClearImage}
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      width: 32,
                      height: 32,
                      bgcolor: alpha(theme.palette.error.main, 0.9),
                      color: 'common.white',
                      '@media (hover: hover)': {
                        '&:hover': {
                          bgcolor: theme.palette.error.main,
                          transform: 'scale(1.1)',
                        },
                      },
                      transition: theme.transitions.create(['background-color', 'transform'], {
                        duration: theme.transitions.duration.shorter,
                      }),
                      zIndex: 10,
                    }}
                    title={t('image.remove')}
                  >
                    <SolarTrashBinTrashBold height={16} width={16} />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: size === 'medium' ? 0 : 1,
                    py: size === 'medium' ? 2 : 3,
                  }}
                >
                  <IconButton
                    color="primary"
                    aria-label="Upload image"
                    component="span"
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      mb: 0.75,
                      '@media (hover: hover)': {
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.16),
                        },
                      },
                    }}
                  >
                    <SolarUploadOutline height={18} width={18} />
                  </IconButton>
                  {size === 'medium' && (
                    <Box sx={{ maxWidth: 280, mx: 'auto' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.main',
                          textAlign: 'center',
                          lineHeight: 1.4,
                          fontSize: '0.8125rem',
                          fontWeight: 400,
                        }}
                      >
                        {t('image.hint.crop_upload_click')}
                      </Typography>
                      {!isMobile && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.main',
                            textAlign: 'center',
                            lineHeight: 1.4,
                            fontSize: '0.8125rem',
                            fontWeight: 400,
                            mt: 0.25,
                          }}
                        >
                          {t('image.hint.canvas_upload_drag', {
                            shortcutKey: isMac() ? ' ⌘ + v ' : ' Ctrl + v ',
                          })}
                        </Typography>
                      )}
                      {/* {(minPixel > 0 || maxPixel < Infinity) && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            textAlign: 'center',
                            fontSize: '0.75rem',
                            fontWeight: 400,
                            display: 'block',
                            opacity: 0.8,
                          }}
                        >
                          ({minPixel || 0}px - {maxPixel === Infinity ? t('image.unlimited') : maxPixel}px)
                        </Typography>
                      )} */}
                    </Box>
                  )}
                </Box>
              )}
            </label>
            {error && (
              <Typography variant="caption" color="error">
                {helperText}
              </Typography>
            )}
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: 'blur(4px)',
                  borderRadius: 2,
                  zIndex: 999,
                }}
              >
                <CircularProgress
                  size={24}
                  sx={{
                    color: 'primary.main',
                  }}
                />
              </Box>
            )}
          </Paper>
        </Box>

        {/* Image Cropping Panel */}
        {imageState.original && (
          <CropPanel
            open={openCropModal}
            image={imageState.original}
            aspectRatio={imageState.aspectRatio} // 传递原始图片宽高比
            onClose={handleCropCancel}
            onComplete={(croppedImage: string) => {
              handleCropComplete(croppedImage);
              setOpenCropModal(false);
            }}
            onLoading={(isLoading: boolean) => setLoading(isLoading)}
          />
        )}
      </>
    );
  },
);

TraditionalModeComponent.displayName = 'TraditionalModeComponent';

// 导出传统裁剪模式组件
export const CropModeComponent = TraditionalModeComponent;
